"use client";

import { useEffect, useRef, useState } from "react";
import { FUSIONS, type FusionCase } from "./data";
import { c, useReducedMotion } from "./hooks";

// Each case plays its 6.4 s forced-fusion sequence once when it scrolls into
// view (28% visible), again on hover / focus, and on "Replay Experiment".
// Replays remount the article so the CSS animations restart from frame 0.

export default function FusionLab() {
  return (
    <section className={c("section", "fusion-lab")} id="fusionLab" aria-labelledby="fusionTitle">
      <div className={c("section-inner")}>
        <div className={c("fusion-heading")}>
          <span className={c("fusion-alert", "pixel-font")}>Crimson Squad Restricted File</span>
          <p className={c("section-kicker", "pixel-font")}>Illegal experiment records</p>
          <h2 id="fusionTitle">Forced Fusion Lab</h2>
          <p>The Crimson Squad steals Mojumon for illegal experiments, forcing two unwilling creatures into a single fused form. These Mojumon never chose this. Tamers must expose the lab—and set them free.</p>
        </div>
        <div className={c("fusion-grid")}>{FUSIONS.map((f) => <Case key={f.code} f={f} />)}</div>
        <p className={c("fusion-note")}><strong>Crimson Squad violation:</strong> Mojumon containment, theft, and forced fusion experimentation.</p>
      </div>
    </section>
  );
}

function Case({ f }: { f: FusionCase }) {
  const reduced = useReducedMotion();
  const [run, setRun] = useState(0); // 0 = never played; each replay bumps
  const article = useRef<HTMLElement>(null);
  // First play when 28% of the card is on screen (the prototype's observer).
  useEffect(() => {
    const el = article.current;
    if (reduced || !("IntersectionObserver" in window) || !el) return;
    const io = new IntersectionObserver((entries) => entries.forEach((e) => { if (e.isIntersecting) { setRun((r) => r || 1); io.disconnect(); } }), { threshold: 0.28 });
    io.observe(el);
    return () => io.disconnect();
  }, [reduced]);
  const replay = () => setRun((r) => r + 1);
  return (
    <article key={run} ref={article} className={c("fusion-case", run > 0 && "is-active")} tabIndex={0} aria-label={f.label}
      onPointerEnter={() => { if (!reduced) replay(); }} onFocus={(e) => { if (!reduced && e.target === e.currentTarget) replay(); }}>
      <span className={c("case-code")}>{f.code}</span>
      <div className={c("fusion-stage")}>
        <img className={c("fusion-parent", "fusion-parent-left")} src={f.left.src} alt={f.left.alt} />
        <span className={c("fusion-core")} aria-hidden="true">Fusing<span className={c("fusion-dots")} /></span>
        <img className={c("fusion-parent", "fusion-parent-right")} src={f.right.src} alt={f.right.alt} />
        <img className={c("fusion-silhouette")} src={f.result.src} alt="" aria-hidden="true" />
        <img className={c("fusion-result")} src={f.result.src} alt={f.result.alt} />
        <span className={c("reveal-sparks")} aria-hidden="true">{Array.from({ length: 12 }, (_, i) => <i key={i} />)}</span>
      </div>
      <div className={c("fusion-meta")}><span>{f.left.name}</span><b>+</b><span>{f.right.name}</span><strong>{f.result.name}</strong></div>
      <p>{f.text}</p>
      <button className={c("replay-fusion")} type="button" onClick={replay}>Replay Experiment</button>
    </article>
  );
}
